import { useState, useEffect } from 'react';

export function useMediaLoader(src, type = 'image') {
  const [state, setState] = useState({
    isLoaded: false,
    isReadyToPlay: false,
    isFullyLoaded: false,
    progress: 0,
    error: null,
  });

  useEffect(() => {
    if (!src) return;

    setState({
      isLoaded: false,
      isReadyToPlay: false,
      isFullyLoaded: false,
      progress: 0,
      error: null,
    });

    let media;
    let timeoutId;

    const updateState = (updates) => {
      setState((prev) => ({ ...prev, ...updates }));
    };

    if (type === 'image') {
      media = new Image();

      media.onload = () => {
        updateState({
          isLoaded: true,
          isFullyLoaded: true,
          progress: 100,
        });
      };

      media.onerror = () => {
        updateState({ error: 'Ошибка загрузки изображения' });
      };

      media.src = src;
    } else if (type === 'video') {
      media = document.createElement('video');
      media.preload = 'auto';
      media.src = src;

      const onLoadedMetadata = () => {
        updateState({ isLoaded: true });
      };

      const onCanPlayThrough = () => {
        updateState({ isReadyToPlay: true });
        // Помечаем как полностью загруженное, если буферизация завершена
        checkBuffering();
      };

      const onError = () => {
        updateState({ error: 'Ошибка загрузки видео' });
      };

      const checkBuffering = () => {
        if (!media) return;

        const duration = media.duration || 1;
        let percent = 0;

        if (media.buffered.length > 0) {
          const bufferedEnd = media.buffered.end(media.buffered.length - 1);
          percent = Math.min(100, (bufferedEnd / duration) * 100);
        }

        const isComplete =
          percent >= 99.9 ||
          media.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA;

        updateState({
          progress: percent,
          isReadyToPlay: true,
          isFullyLoaded: isComplete,
        });

        if (!isComplete) {
          // Продолжаем проверять буферизацию
          requestAnimationFrame(checkBuffering);
        } else {
          clearTimeout(timeoutId);
        }
      };

      timeoutId = setTimeout(() => {
        updateState((prev) => ({
          isFullyLoaded: !prev.isFullyLoaded,
          progress: 100,
          error: prev.isFullyLoaded ? null : 'Загрузка завершена по таймауту',
        }));
      }, 30000);

      media.addEventListener('loadedmetadata', onLoadedMetadata);
      media.addEventListener('canplaythrough', onCanPlayThrough);
      media.addEventListener('error', onError);

      // Начинаем проверять буферизацию
      const progressCheck = () => checkBuffering();
      media.addEventListener('progress', progressCheck);

      const cleanup = () => {
        clearTimeout(timeoutId);
        if (media) {
          media.removeEventListener('loadedmetadata', onLoadedMetadata);
          media.removeEventListener('canplaythrough', onCanPlayThrough);
          media.removeEventListener('progress', progressCheck);
          media.removeEventListener('error', onError);
          media.removeAttribute('src');
          media.load();
        }
      };

      return cleanup;
    }
  }, [src, type]);

  return state;
}
